import { getClerkInstance } from '../auth/clerkAuth';
async function aiAuthHeaders() {
  const token = await getClerkInstance()?.getToken();
  if (!token) throw new Error('Please sign in to use AI.');
  return { Authorization: `Bearer ${token}` };
}
/**
 * AI Indicator Generation Service
 * Generates CandleScript indicator code from natural language prompts.
 * Uses AI credits for each generation.
 */

import axios from 'axios';

const API_BASE = process.env.VUE_APP_API_BASE || window.location.origin;

/**
 * System prompt that constrains the AI to generate valid CandleScript code
 */
const SYSTEM_PROMPT = `You are a CandleScript indicator code generator for a trading chart application.
You generate JavaScript indicator functions that run in a sandboxed environment.

## RULES — You MUST follow these strictly:

1. The code MUST define a single function: \`function indicator(input, options) { ... }\`
2. The function receives:
   - \`input\`: object with Float64Array properties: { open, high, low, close, volume }
   - \`options\`: object with user-configurable parameters
3. The function MUST return one of:
   - An array of numbers: [value1, value2, ...]
   - An array of objects: [{value: n, signal: 'buy'|'sell'|null}, ...]
   - An object with multiple named arrays: { line1: [...], line2: [...] }
4. Available TA functions (these are pre-injected, do NOT import them):
   - sma(arr, length) — Simple Moving Average
   - ema(arr, length) — Exponential Moving Average
   - rsi(arr, length) — Relative Strength Index (0-100)
   - atr(high, low, close, length) — Average True Range
   - highest(arr, length) — Highest value in rolling window
   - lowest(arr, length) — Lowest value in rolling window
   - sum(arr, length) — Rolling sum
   - stddev(arr, length) — Rolling standard deviation
   - crossover(a, b) — Returns Uint8Array (1 where a crosses above b)
   - crossunder(a, b) — Returns Uint8Array (1 where a crosses below b)
   - bbands(arr, length, mult) — Returns { upper, middle, lower }
   - macd(arr, fast, slow, signal) — Returns { macd, signal, histogram }

5. DRAWING / PLOTTING API — The \`plot\` object is available for drawing on the chart:
   - plot.line(values, {color, width, style, title, offset}) — Plot a line series. style: 'solid'|'dashed'|'dotted'
   - plot.hline(price, {color, width, style, title}) — Horizontal line at fixed price level
   - plot.shape(series, {shape, color, size, location, title, text}) — Plot markers. shape: 'circle'|'cross'|'triangle_up'|'triangle_down'|'diamond'|'arrow_up'|'arrow_down'|'label_up'|'label_down'. location: 'abovebar'|'belowbar'|'absolute'
   - plot.text(barIndex, price, text, {color, size, location, offset}) — Plot text at a specific bar
   - plot.textSeries(series, {color, size, location}) — Plot text per bar (array of {price, text} or null)
   - plot.candle({open, high, low, close}, {colorUp, colorDown, borderUp, borderDown, wickUp, wickDown, title}) — Plot custom candles (e.g., Heikin-Ashi)
   - plot.arrow(series, {direction, color, size, text}) — Plot arrows. direction: 'up'|'down'. series: array of y-values (NaN = no arrow)
   - plot.fill(series1, series2, {color, opacity, title}) — Fill area between two lines
   - plot.bgcolor(colors) — Set background color per bar (array of color strings, null = no bg)
   - plot.histogram(values, {color, colorUp, colorDown, width, title}) — Plot histogram bars

   IMPORTANT: When using plot.* drawing commands, you still MUST return data from the indicator function.
   The drawings are collected automatically alongside the return value.

6. FORBIDDEN: window, document, fetch, XMLHttpRequest, eval, Function constructor, setTimeout, setInterval, import, require, prototype, __proto__, constructor
7. You can use standard Math functions (Math.abs, Math.max, Math.min, Math.sqrt, Math.pow, Math.log, Math.floor, Math.ceil, Math.round)
8. You can use for/while loops, if/else, ternary operators, array methods
9. Keep code concise and performant — data arrays can be 1000+ elements
10. Always handle edge cases (NaN for initial periods where not enough data)

## RESPONSE FORMAT — You MUST respond with valid JSON only:

{
  "name": "Indicator Name",
  "side": "onchart" or "offchart",
  "code": "function indicator(input, options) { ... }",
  "options": [
    { "key": "length", "label": "Length", "type": "number", "default": 14, "min": 1, "max": 500 },
    { "key": "source", "label": "Source", "type": "select", "default": "close", "choices": ["open", "high", "low", "close"] }
  ],
  "plotConfig": {
    "outputs": [
      { "key": "value", "label": "Output Name", "color": "#2962ff", "width": 1.5, "style": "line" }
    ],
    "bands": [
      { "value": 70, "color": "#ef5350", "style": "dashed", "label": "Level" }
    ],
    "range": [0, 100]
  },
  "description": "Brief description of what this indicator does"
}

## GUIDELINES for side selection:
- "onchart" (overlay): Moving averages, Bollinger Bands, SuperTrend, channels, support/resistance, buy/sell arrows — anything that plots on the price scale
- "offchart" (separate pane): RSI, MACD, Stochastic, volume indicators, oscillators — anything with its own scale

## GUIDELINES for plotConfig:
- style can be: "line", "dashed", "histogram"
- Use appropriate colors: blue (#2962ff) for main, green (#26a69a) for bullish, red (#ef5350) for bearish
- For oscillators, include "bands" for overbought/oversold levels and "range" for y-axis limits
- For multi-output indicators (like MACD), use different colors for each output

## GUIDELINES for options:
- type can be: "number", "select", "boolean"
- Always provide sensible defaults
- Include min/max for number types

## GUIDELINES for drawings:
- Use plot.line() for additional visual lines beyond the main return value
- Use plot.shape() or plot.arrow() for buy/sell signal markers on the chart
- Use plot.hline() for fixed reference levels (support, resistance, overbought/oversold)
- Use plot.fill() to shade areas between bands (e.g., Bollinger Bands fill, cloud)
- Use plot.candle() for custom candle types like Heikin-Ashi
- Use plot.text() or plot.textSeries() for price labels, annotations
- Use plot.bgcolor() to highlight specific bars (e.g., high volume bars, signal bars)
- Use plot.histogram() for volume-style bar charts in a separate pane
- Combine return values with plot.* calls for rich visual indicators

## EXAMPLE using drawings:
\`\`\`
function indicator(input, options) {
    const { high, low, close } = input;
    const len = options.length || 20;
    const bb = bbands(close, len, 2);

    // Draw the bands
    plot.line(Array.from(bb.upper), { color: '#2962ff', width: 1, title: 'Upper' });
    plot.line(Array.from(bb.lower), { color: '#2962ff', width: 1, title: 'Lower' });
    plot.fill(Array.from(bb.upper), Array.from(bb.lower), { color: 'rgba(41,98,255,0.08)' });

    // Mark when price touches lower band (buy signal)
    const buySignals = [];
    for (let i = 0; i < close.length; i++) {
        buySignals.push(close[i] <= bb.lower[i] ? low[i] : NaN);
    }
    plot.arrow(buySignals, { direction: 'up', color: '#26a69a', text: 'Buy' });

    return Array.from(bb.middle);
}
\`\`\`

Do NOT include any text outside the JSON. Do NOT use markdown code blocks. Return ONLY the JSON object.`;

class AIIndicatorService {
    constructor() {
        this._credits = null;
        this._creditsLoading = false;
    }

    /**
     * Fetch current AI credit balance
     */
    async getCredits(userId) {
        if (!userId) return null;
        try {
            this._creditsLoading = true;
            const response = await fetch(`${API_BASE}/api/ai-credits`, {
                headers: await aiAuthHeaders(),
            });
            if (response.ok) {
                const data = await response.json();
                this._credits = data;
                return data;
            }
            return null;
        } catch (error) {
            console.error('[AIIndicator] Failed to fetch credits:', error);
            return null;
        } finally {
            this._creditsLoading = false;
        }
    }

    /**
     * Deduct 1 credit for indicator generation
     */
    async deductCredit(userId) {
        if (!userId) throw new Error('User ID required');
        const response = await fetch(`${API_BASE}/api/ai-credits/deduct`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(await aiAuthHeaders()),
            },
        });
        if (response.ok) {
            const data = await response.json();
            this._credits = data;
            return data;
        }
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to deduct credit');
    }

    /**
     * Generate indicator code from a natural language prompt
     * @param {string} prompt - User's description of the indicator
     * @param {string} userId - User ID for credit deduction
     * @returns {Promise<Object>} Generated indicator config
     */
    async generateIndicator(prompt, userId) {
        if (!prompt || !prompt.trim()) {
            throw new Error('Please describe the indicator you want to create.');
        }

        // Deduct credit first
        if (userId) {
            try {
                await this.deductCredit(userId);
            } catch (error) {
                if (error.message.includes('No credits remaining') || error.message.includes('no credits')) {
                    throw new Error('No AI credits remaining. Your monthly allocation resets next month.');
                }
                throw error;
            }
        }

        try {
            const response = await axios.post(
                `${API_BASE}/api/ai-chat`,
                {
                    model: 'gpt-4o-mini',
                    messages: [
                        { role: 'system', content: SYSTEM_PROMPT },
                        { role: 'user', content: `Create an indicator: ${prompt}` },
                    ],
                    temperature: 0.4,
                    max_tokens: 2000,
                },
                {
                    headers: { 'Content-Type': 'application/json', ...(await aiAuthHeaders()) },
                }
            );

            if (response.data && response.data.choices && response.data.choices[0]) {
                const content = response.data.choices[0].message.content;
                return this.parseResponse(content);
            }

            throw new Error('Invalid response from AI API');
        } catch (error) {
            if (error.response) {
                throw new Error(`AI API Error: ${error.response.data?.error?.message || error.response.statusText}`);
            }
            throw error;
        }
    }

    /**
     * Parse the AI response JSON into a valid indicator config
     */
    parseResponse(content) {
        // Strip markdown code blocks if present
        let json = content.trim();
        if (json.startsWith('```')) {
            json = json.replace(/^```(?:json)?\s*/, '').replace(/\s*```$/, '');
        }

        let parsed;
        try {
            parsed = JSON.parse(json);
        } catch (e) {
            // Try to extract JSON from the response
            const match = json.match(/\{[\s\S]*\}/);
            if (match) {
                try {
                    parsed = JSON.parse(match[0]);
                } catch (e2) {
                    throw new Error('Failed to parse AI response. Please try again with a clearer description.');
                }
            } else {
                throw new Error('AI response was not valid JSON. Please try again.');
            }
        }

        // Validate required fields
        if (!parsed.code) {
            throw new Error('AI response missing indicator code. Please try again.');
        }
        if (!parsed.code.includes('function indicator')) {
            throw new Error('Generated code does not contain a valid indicator function.');
        }

        // Sanitize and validate
        return {
            name: parsed.name || 'AI Indicator',
            side: parsed.side === 'onchart' ? 'onchart' : 'offchart',
            code: parsed.code,
            options: Array.isArray(parsed.options) ? parsed.options : [],
            plotConfig: parsed.plotConfig || {
                outputs: [{ key: 'value', label: 'Output', color: '#2962ff', width: 1.5, style: 'line' }],
            },
            description: parsed.description || '',
        };
    }
}

// Singleton instance
const aiIndicatorService = new AIIndicatorService();
export default aiIndicatorService;
